import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import api from '../config/axios';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';

const ForgotPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await api.post('/auth/forgot-password', {
        email: data.email,
      });
      
      setEmailSent(true);
      toast.success('Password reset email sent successfully!');
    } catch (error) {
      console.error('Forgot password error:', error);
      const message = error.response?.data?.message || 'Failed to send reset email';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <Logo className="h-10" showText={true} variant="dark" />
            </div>
            <h2 className="text-4xl font-bold text-neutral-900 mb-3">
              Check your email
            </h2>
            <p className="text-lg text-neutral-600">
              We've sent a password reset link to your email address
            </p>
          </div>

          {/* Success Message */}
          <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-8 text-center">
            <div className="mx-auto flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h3 className="text-xl font-semibold text-neutral-900 mb-4">
              Reset link sent!
            </h3>

            <p className="text-neutral-600 mb-6">
              We've sent a password reset link to your email address.
              The link will expire in 1 hour for security reasons.
            </p>

            <div className="space-y-4">
              <p className="text-sm text-neutral-500">
                Didn't receive the email? Check your spam folder or
              </p>

              <button
                onClick={() => setEmailSent(false)}
                className="btn-outline w-full"
              >
                Try again
              </button>
            </div>
          </div>

          {/* Back to Login */}
          <div className="text-center">
            <p className="text-neutral-600 text-base">
              Remember your password?{' '}
              <Link to="/login" className="font-semibold text-primary-500 hover:text-primary-600 transition-colors">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-8">
            <Logo className="h-10" showText={true} variant="dark" />
          </div>
          <h2 className="text-3xl font-bold text-neutral-800 mb-3">
            Forgot password?
          </h2>
          <p className="text-neutral-500">
            Enter your email address and we'll send you a reset link
          </p>
        </div>

        {/* Forgot Password Form */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-8">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="input-label">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={`input-field ${errors.email ? 'input-error' : ''}`}
                placeholder="name@company.com"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
              />
              {errors.email && (
                <p className="input-error-message">{errors.email.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="spinner spinner-sm border-white/30 border-t-white"></div>
                  <span>Sending reset link...</span>
                </div>
              ) : (
                'Send reset link'
              )}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-neutral-500">
              We'll send you an email with a link to reset your password.
            </p>
          </div>
        </div>

        {/* Back to Login */}
        <div className="text-center">
          <p className="text-neutral-600 text-base">
            Remember your password?{' '}
            <Link to="/login" className="font-medium text-primary-500 hover:text-primary-600 transition-colors">
              Sign in here
            </Link>
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-neutral-400">
          &copy; 2025 Chatza. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword; 